import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { injectIntl } from 'react-intl';
import { generatePath, useHistory } from 'react-router-dom';
import { CircularProgress } from '@material-ui/core';
import { SERVICES_PROPERTIES } from 'app/config/services';
import { FormServiceContext } from 'app/utils/contexts';
import { setupSurveyInitialValues, setupSurveyPayload } from 'app/utils/forms/services/surveys';
import { useDispatch, useSelector } from 'react-redux';
import vehiclesEndpoint from 'app/api/services/vehiclesEndpoint';
import { setVehiclePlateByCompany } from 'app/store/slices/vehicle';
import notification from 'app/config/notification';
import { URLS } from 'app/config/urls';
import { errorHandler } from 'app/utils/errorHandler';
import { validateVehicleRequest } from 'app/utils/forms/validators/vehicle';

const Survey = props => {
  const dispatch = useDispatch();
  const history = useHistory();
  const {
    intl,
    classes,
    loading,
    loadingDocuments,
    setLoadingDocuments,
    onCancel,
    onSave,
    onSubmit,
    initialValues,
    setInitialValues,
  } = props;

  const internalNewRequest = useSelector(state => state.forms.internalNewRequest);
  const vehicles = useSelector(state => state.forms.vehicles);
  const request = useSelector(state => state.requests.details);
  const authUser = useSelector(state => state.auth.user);

  // eslint-disable-next-line no-unused-vars
  const [canEdit, setCanEdit] = useState(true);
  const [errorsList, setErrorsList] = useState({});
  const [loadingPlates, setLoadingPlates] = useState(false);

  useEffect(() => {
    const init = async () => {
      if (Object.keys(internalNewRequest).length > 0) {
        const temp = setupSurveyInitialValues(internalNewRequest);
        setInitialValues(temp);
      } else {
        if (SERVICES_PROPERTIES[internalNewRequest.cdServico]) {
          setInitialValues(SERVICES_PROPERTIES[internalNewRequest.cdServico].formInitialValues);
        }
      }

      if (internalNewRequest.company && internalNewRequest.company.cdEmpresa) {
        setLoadingPlates(true);
        try {
          let response = [];
          response = await vehiclesEndpoint.getVehiclesPlatesByCompany(internalNewRequest.company.cdEmpresa);
          if (response.data) {
            dispatch(setVehiclePlateByCompany(response.data));
          }
          if (!response.data || response.data.length <= 0) {
            if (!request.situacao) {
              notification(
                intl.formatMessage({ id: 'MESSAGES.SERVICE.FORMS.SURVEY_NO_VEHICLES_FOUND' }),
                {
                  toastId: 'MESSAGES.SERVICE.FORMS.SURVEY_NO_VEHICLES_FOUND',
                  type: 'error',
                },
              );
              history.push(generatePath(URLS.SERVICES));
            }
          }
        } catch (error) {
          errorHandler(error.response?.status, error.response?.data?.message);
        }
        setLoadingPlates(false);
      }

    };
    init();
  }, [dispatch, history, internalNewRequest, intl, request.situacao, setInitialValues]);

  const validationSchema = useMemo(() => {
    return SERVICES_PROPERTIES[internalNewRequest.cdServico]
      ? SERVICES_PROPERTIES[internalNewRequest.cdServico].validationSchema
      : {};
  }, [internalNewRequest.cdServico]);

  const setupPayload = useCallback(
    (valuesCopy, requestJsonData) => {
      const payload = setupSurveyPayload({ values: valuesCopy, requestJsonData, vehicles });
      if (!validateVehicleRequest({ values: payload, intl, isInternalForm: null, survey: true })) {
        return;
      }
      return payload;
    },
    [intl, vehicles],
  );

  const handleSave = useCallback(
    (values, fromSubmit, oldData) => {
      onSave(values, fromSubmit, oldData, setupPayload);
    },
    [onSave, setupPayload],
  );

  const handleSubmit = useCallback(
    (values) => {
      onSubmit(values, onSave, setupPayload);
    },
    [onSave, onSubmit, setupPayload],
  );

  const contextProvider = useMemo(() => {
    return {
      isInternalForm: false,
      hideActionBtn: true,
      hideFinalizeBtn: false,
      mainColor: 'secondary',
      loadingDocuments,
      setLoadingDocuments,
      onCancel,
      onSave: handleSave,
      onSubmit: handleSubmit,
      initialValues,
      validationSchema,
      validateOnSave: true,
      canEdit,
      user: authUser,
      errorsList,
      setErrorsList,
      loadingPlates,
    };
  }, [
    authUser,
    canEdit,
    errorsList,
    handleSave,
    handleSubmit,
    initialValues,
    loadingDocuments,
    onCancel,
    setLoadingDocuments,
    validationSchema,
    loadingPlates,
  ]);

  const renderRequest = () => {
    const internalCdServico = internalNewRequest.cdServico || request.cdServico;
    let Component = SERVICES_PROPERTIES[internalCdServico] ? (
      SERVICES_PROPERTIES[internalCdServico].component
    ) : (
      <></>
    );

    return (
      <>
        {loading && (
          <div className={classes.centerProgress}>
            <CircularProgress size={20} />
          </div>
        )}
        {!loading && (
          <FormServiceContext.Provider value={contextProvider}>
            <Component {...props} />
          </FormServiceContext.Provider>
        )}
      </>
    );
  };

  return renderRequest();
};

export default injectIntl(Survey);
